/**
 * Component Loader
 *
 * A dynamic component renderer that renders components from configuration objects.
 * This component provides a flexible way to render various UI components based on
 * configuration data, enabling dynamic UIs for integration settings.
 *
 * Features:
 * - Dynamic rendering from configuration
 * - Multiple component types (button, switch, text field, icon, account, text)
 * - Flexible layout options (vertical, horizontal, grid)
 * - Component registry system
 * - Style and class customization
 *
 * @example
 * ```html
 * <uap-app-integration-settings-component-loader
 *     .config=${{
 *         components: [
 *             { type: 'text', data: 'Hello World' },
 *             { type: 'button', data: { label: 'Click me' } }
 *         ],
 *         layout: 'horizontal'
 *     }}>
 * </uap-app-integration-settings-component-loader>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _AutomatorAppIntegrationSettingsComponentLoader_config_accessor_storage;
// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
// Import styles
import styles from './web-component-style.scss?inline';
// Import component definitions
import { buttonRenderer } from './components/button';
import { switchRenderer } from './components/switch';
import { textFieldRenderer } from './components/text-field';
import { textRenderer } from './components/text';
import { iconRenderer } from './components/icon';
import { accountRenderer } from './components/account';
import { sectionRenderer } from './components/section';
// Component registry
const componentRegistry = {
    button: buttonRenderer,
    switch: switchRenderer,
    'text-field': textFieldRenderer,
    text: textRenderer,
    icon: iconRenderer,
    account: accountRenderer,
    section: sectionRenderer
};
/**
 * Component Loader
 * @element uap-app-integration-settings-component-loader
 */
let AutomatorAppIntegrationSettingsComponentLoader = class AutomatorAppIntegrationSettingsComponentLoader extends LitElement {
    constructor() {
        super(...arguments);
        _AutomatorAppIntegrationSettingsComponentLoader_config_accessor_storage.set(this, { components: [] });
    }
    /**
     * Disable Shadow DOM for better integration
     *
     * @return {this} The render root
     */
    createRenderRoot() {
        return this;
    }
    /**
     * The loader configuration to render
     *
     * @type {LoaderConfig}
     */
    get config() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsComponentLoader_config_accessor_storage, "f"); }
    set config(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsComponentLoader_config_accessor_storage, value, "f"); }
    /**
     * Render the components based on configuration
     */
    render() {
        const { components, layout = 'vertical', className, style } = this.config;
        if (!components || components.length === 0) {
            return html ``;
        }
        const containerClass = `component-loader component-loader--${layout} ${className || ''}`;
        return html `
			<div class="${containerClass}" style="${style || ''}">
				${components.map((component, index) => this.renderComponent(component, index))}
			</div>
		`;
    }
    /**
     * Render a single component based on its configuration
     *
     * @param component - The component configuration
     * @param index - The component index
     *
     * @return {TemplateResult} The rendered component
     */
    renderComponent(component, index) {
        const { type, data, className, style, ...restProps } = component;
        const renderer = componentRegistry[type];
        if (!renderer) {
            console.warn(`Unknown component type: ${type}`);
            return html ``;
        }
        const componentClass = `component-loader__component component-loader__component--${type} ${className || ''}`;
        return html `
			<div class="${componentClass}" style="${style || ''}" data-component-index="${index}">
				${renderer(data, restProps)}
			</div>
		`;
    }
};
_AutomatorAppIntegrationSettingsComponentLoader_config_accessor_storage = new WeakMap();
AutomatorAppIntegrationSettingsComponentLoader.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({
        type: Object,
        converter: {
            fromAttribute: (value) => {
                if (!value) {
                    return { components: [] };
                }
                try {
                    return JSON.parse(value);
                }
                catch {
                    console.error('Failed to parse component loader configuration');
                    return { components: [] };
                }
            }
        }
    })
], AutomatorAppIntegrationSettingsComponentLoader.prototype, "config", null);
AutomatorAppIntegrationSettingsComponentLoader = __decorate([
    customElement('uap-app-integration-settings-component-loader')
], AutomatorAppIntegrationSettingsComponentLoader);
export { AutomatorAppIntegrationSettingsComponentLoader };
